import { Check, X, ChevronRight } from "lucide-react";
import { useNavigate } from 'react-router';

// No police-clearance row: Kanfy does not renew clearances on a fixed cycle,
// so a "renewed every N months" claim here was untrue (owner, 19 Oct).
const comparisonRows = [
  {
    feature: "Identity verified",
    trusted: "National ID checked",
    direct: "Rarely confirmed",
  },
  {
    feature: "Secure payment",
    trusted: "In-platform, receipted",
    direct: "Cash, no receipt",
  },
  {
    feature: "Problem resolution",
    trusted: "24-hour chat support + buyer protection",
    direct: "On your own",
  },
  {
    feature: "Booking history",
    trusted: "Digital record always",
    direct: "Paper or verbal",
  },
  {
    feature: "Performance tracked",
    trusted: "Verified reviews + ratings",
    direct: "Word of mouth only",
  },
];

export function PremiumComparisonTable() {
  const navigate = useNavigate();
  const rows = comparisonRows;

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Heading */}
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-semibold tracking-widest text-red-700 dark:text-primary uppercase mb-3 bg-primary/10 px-4 py-1.5 rounded-full">
            Why Us
          </span>
          <h2 className="h-page text-foreground mb-3">
            Why Kanfy?
          </h2>
          <p className="text-muted-foreground text-base max-w-xl mx-auto">
            Hiring directly feels simpler — until something goes wrong.
          </p>
        </div>

        {/* Desktop 3-column table */}
        <div className="hidden md:block overflow-hidden rounded-2xl border border-border shadow-sm">
          {/* Header */}
          <div className="grid grid-cols-3 bg-kanfy-ink dark:bg-muted text-white">
            <div className="px-6 py-4 text-sm font-semibold text-white/60 uppercase tracking-wide">
              Feature
            </div>
            <div className="px-6 py-4 text-sm font-bold text-center text-emerald-400 uppercase tracking-wide border-l border-white/10">
              ✓ Kanfy
            </div>
            <div className="px-6 py-4 text-sm font-semibold text-center text-white/50 uppercase tracking-wide border-l border-white/10">
              Hiring Directly
            </div>
          </div>

          {/* Rows */}
          {rows.map((row, i) => (
            <div
              key={i}
              className={`grid grid-cols-3 border-t border-border ${i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}`}
            >
              <div className="px-6 py-4 text-sm font-medium text-foreground flex items-center">
                {row.feature}
              </div>
              <div className="px-6 py-4 border-l border-border flex items-center gap-2 text-sm text-emerald-700 font-medium">
                <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                {row.trusted}
              </div>
              <div className="px-6 py-4 border-l border-border flex items-center gap-2 text-sm text-muted-foreground">
                <X className="h-4 w-4 text-muted-foreground shrink-0" />
                {row.direct}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile card-per-row layout */}
        <div className="md:hidden space-y-3">
          {rows.map((row, i) => (
            <div
              key={i}
              className="rounded-2xl border border-border bg-white overflow-hidden shadow-sm"
            >
              <div className="px-4 py-2.5 bg-slate-50 border-b border-border">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {row.feature}
                </span>
              </div>
              <div className="grid grid-cols-2 divide-x divide-border">
                <div className="px-4 py-3">
                  <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide mb-1">
                    Kanfy
                  </p>
                  <div className="flex items-start gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    <span className="text-xs text-foreground font-medium leading-snug">
                      {row.trusted}
                    </span>
                  </div>
                </div>
                <div className="px-4 py-3">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mb-1">
                    Hiring Directly
                  </p>
                  <div className="flex items-start gap-1.5">
                    <X className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <span className="text-xs text-muted-foreground leading-snug">
                      {row.direct}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-8">
          <button
            type="button"
            onClick={() => navigate("/booking-flow")}
            className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-6 py-3 rounded-xl hover:bg-primary/90 transition-colors text-sm"
          >
            Find a Verified Helper
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
